import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { InventoryItemWithPricing } from '../common/inventory-item';
import { ItemRow } from '../item-pricing-table/item-pricing-table.interface';
import { CreateLineItemFromItemRow } from '../item-pricing-table/line-item';

export type ChangedInventoryItem = InventoryItemWithPricing & { index: number };

export interface BillingTermCallbackOutput {
  startDate: Date;
  endDate?: Date;
  addAnotherTerm?: boolean;
}

export type BillingTermCallbackFn = (editing?: boolean) => Observable<BillingTermCallbackOutput>;

export interface ItemsChangedEvent {
  items: ChangedInventoryItem[];
  type: ItemsChangedEventType;
}

export type ItemsChangedEventType = 'add' | 'update' | 'delete';

@Injectable()
export class ItemPricingTableSelectorService {
  private _items: WritableSignal<InventoryItemWithPricing[]> = signal<InventoryItemWithPricing[]>([]);
  items: Signal<InventoryItemWithPricing[]> = this._items.asReadonly();
  private _events: WritableSignal<ItemsChangedEvent[]> = signal<ItemsChangedEvent[]>([]);
  events: Signal<ItemsChangedEvent[]> = this._events.asReadonly();

  setItems(items: InventoryItemWithPricing[]): void {
    if (!items?.length) {
      return;
    }

    this._items.set(items);
  }

  addItems(newItems: InventoryItemWithPricing[]): void {
    if (!newItems?.length) {
      return;
    }

    let lastItemIndex = this._items().length - 1;
    const changedItems = newItems.map((item) => {
      lastItemIndex += 1;
      return { ...item, index: lastItemIndex };
    });
    this.updateItems((items) => items.concat(newItems), changedItems, 'add');
  }

  addItemAtIndex(newItem: InventoryItemWithPricing, index: number): void {
    const nItems = this._items().length;
    if (!this.isItemIndexValid(index) && index !== nItems) {
      return;
    }

    this.updateItems(
      (items) => {
        items.splice(index, 0, newItem);
        return items;
      },
      [{ ...newItem, index }],
      'add',
    );
  }

  setItemMatchingRow(changedItem: InventoryItemWithPricing, itemRow: ItemRow): void {
    const index = itemRow.index ?? -1;
    if (!this.isItemIndexValid(index)) {
      return;
    }

    this.updateItems(
      (items) => {
        items[index] = changedItem;
        return items;
      },
      [{ ...changedItem, index }],
      'update',
    );
  }

  setItemsMatchingRows(changedItems: InventoryItemWithPricing[], itemRows: ItemRow[]): void {
    if (changedItems.length !== itemRows.length) {
      return;
    }

    this.updateItems(
      (items) => {
        changedItems.forEach((changedItem, i) => {
          const itemRow = itemRows[i];
          const index = itemRow.index ?? -1;
          if (!this.isItemIndexValid(index)) {
            return;
          }

          items[index] = changedItem;
        });
        return items;
      },
      changedItems.map((changedItem, i) => {
        const itemRow = itemRows[i];
        return {
          ...changedItem,
          pricing: CreateLineItemFromItemRow(itemRow),
          index: itemRow.index ?? -1,
        };
      }),
      'update',
    );
  }

  removeItemMatchingRow(itemRow: ItemRow): void {
    const index = itemRow.index ?? -1;
    if (!this.isItemIndexValid(index)) {
      return;
    }

    const changedItem = this.getItemWithIndex(index);
    this.updateItems(
      (items) => {
        items.splice(index, 1);
        return items;
      },
      [changedItem],
      'delete',
    );
  }

  addNewBillingTermForItemMatchingRow(itemRow: ItemRow, billingTermCallback: BillingTermCallbackFn): void {
    const index = itemRow.index ?? -1;
    if (!this.isItemIndexValid(index)) {
      return;
    }

    const item = this.getItemWithIndex(index);
    const itemCopy = structuredClone(item);
    const indexForCopy = index + 1;
    billingTermCallback().subscribe((output) => {
      if (!output) {
        return;
      }

      if (!itemCopy.pricing) itemCopy.pricing = {};
      if (!itemCopy.pricing.billingPeriod) itemCopy.pricing.billingPeriod = {};
      itemCopy.pricing.billingPeriod.startDate = output.startDate;
      itemCopy.pricing.billingPeriod.endDate = output.endDate ?? undefined;

      this.addItemAtIndex(itemCopy, indexForCopy);

      if (output.addAnotherTerm) {
        const itemRowCopy = structuredClone(itemRow);
        itemRowCopy.index = indexForCopy;
        this.addNewBillingTermForItemMatchingRow(itemRowCopy, billingTermCallback);
      }
    });
  }

  editBillingTermForItemMatchingRow(itemRow: ItemRow, billingTermCallback: BillingTermCallbackFn): void {
    const index = itemRow.index ?? -1;
    if (!this.isItemIndexValid(index)) {
      return;
    }

    const item = this.getItemWithIndex(index);
    billingTermCallback(true).subscribe((output) => {
      if (!output) {
        return;
      }

      this.setItemMatchingRow(
        {
          ...item,
          pricing: {
            billingPeriod: {
              startDate: output.startDate,
              endDate: output.endDate ?? undefined,
            },
          },
        },
        itemRow,
      );

      if (output.addAnotherTerm) {
        this.addNewBillingTermForItemMatchingRow(itemRow, billingTermCallback);
      }
    });
  }

  updateItemsWithPricing(itemRows: ItemRow[]): void {
    const items = this._items();
    const changedItems: InventoryItemWithPricing[] = [];
    const changedRows: ItemRow[] = [];
    items.forEach((item, index) => {
      const itemRow = itemRows[index];
      const lineItem = CreateLineItemFromItemRow(itemRow);
      if (!_.isEqual(item.pricing, lineItem)) {
        item.pricing = lineItem;
        changedItems.push(item);
        changedRows.push(itemRow);
      }
    });
    if (!changedItems.length || !changedRows.length) {
      return;
    }
    this.setItemsMatchingRows(changedItems, changedRows);
  }

  private isItemIndexValid(index: number): boolean {
    return index >= 0 && index < this._items().length;
  }

  private getItemWithIndex(index: number): ChangedInventoryItem {
    const item = this._items()[index];
    return {
      ...item,
      index,
    };
  }

  private updateItems(
    updateFn: UpdateItemsFn,
    changedItems: ChangedInventoryItem[],
    eventType: ItemsChangedEventType,
  ): void {
    // Create a new array because change detection doens't detect a change to an array's items, only the reference.
    this._items.update((items) => [...updateFn(items)]);
    this._events.update((events) => [...events, { items: changedItems, type: eventType }]);
  }
}

type UpdateItemsFn = (items: InventoryItemWithPricing[]) => InventoryItemWithPricing[];
